import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SEO } from "@/components/SEO";

export default function PrivacyPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="privacy-page">
      <SEO
        title="Privacy Policy"
        description="Giant Fitness Privacy Policy. Learn how we collect, use, and protect your personal information. Last updated January 2026."
        url="/privacy"
        type="article"
      />
      <section className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-8" data-testid="privacy-heading">
              PRIVACY POLICY
            </h1>

            <div className="prose max-w-none">
              <p className="text-gray-600 mb-8">
                Last updated: January 2026
              </p>

              <div className="space-y-8 text-gray-700">
                <section>
                  <p className="mb-4">
                    Giant Fitness (&quot;us&quot;, &quot;we&quot;, or &quot;our&quot;) operates the Giant Fitness website (the &quot;Service&quot;).
                  </p>
                  <p className="mb-4">
                    This page informs you of our policies regarding the collection, use and disclosure of Personal Information when you use our Service.
                  </p>
                  <p className="mb-4">
                    We will not use or share your information with anyone except as described in this Privacy Policy.
                  </p>
                  <p className="mb-4">
                    We use your Personal Information for providing and improving the Service. By using the Service, you agree to the collection and use of information in accordance with this policy. Unless otherwise defined in this Privacy Policy, terms used in this Privacy Policy have the same meanings as in our Terms and Conditions, accessible at <Link to="/terms" className="text-brand-orange hover:underline">giantfitnessclubs.com</Link>.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Information Collection And Use</h2>
                  <p className="mb-4">
                    While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. Personally identifiable information (&quot;Personal Information&quot;) may include, but is not limited to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
                    <li>Name</li>
                    <li>Email address</li>
                    <li>Telephone number</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Log Data</h2>
                  <p className="text-gray-700">
                    We collect information that your browser sends whenever you visit our Service (&quot;Log Data&quot;). This Log Data may include information such as your computer&apos;s Internet Protocol (&quot;IP&quot;) address, browser type, browser version, the pages of our Service that you visit, the time and date of your visit, the time spent on those pages and other statistics.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Cookies</h2>
                  <p className="mb-4 text-gray-700">
                    Cookies are files with small amount of data, which may include an anonymous unique identifier. Cookies are sent to your browser from a web site and stored on your computer&apos;s hard drive.
                  </p>
                  <p className="text-gray-700">
                    We use &quot;cookies&quot; to collect information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Service.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Service Providers</h2>
                  <p className="mb-4 text-gray-700">
                    We may employ third party companies and individuals to facilitate our Service, to provide the Service on our behalf, to perform Service-related services or to assist us in analyzing how our Service is used.
                  </p>
                  <p className="text-gray-700">
                    These third parties have access to your Personal Information only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Security</h2>
                  <p className="text-gray-700">
                    The security of your Personal Information is important to us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Information, we cannot guarantee its absolute security.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Links To Other Sites</h2>
                  <p className="mb-4 text-gray-700">
                    Our Service may contain links to other sites that are not operated by us. If you click on a third party link, you will be directed to that third party&apos;s site. We strongly advise you to review the Privacy Policy of every site you visit.
                  </p>
                  <p className="text-gray-700">
                    We have no control over, and assume no responsibility for the content, privacy policies or practices of any third party sites or services.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Children&apos;s Privacy</h2>
                  <p className="mb-4 text-gray-700">
                    Our Service does not address anyone under the age of 18 (&quot;Children&quot;).
                  </p>
                  <p className="text-gray-700">
                    We do not knowingly collect personally identifiable information from children under 18. If you are a parent or guardian and you are aware that your child has provided us with Personal Information, please contact us. If we discover that a child under 18 has provided us with Personal Information, we will delete such information from our servers immediately.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Compliance With Laws</h2>
                  <p className="text-gray-700">
                    We will disclose your Personal Information where required to do so by law or subpoena.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Changes To This Privacy Policy</h2>
                  <p className="mb-4 text-gray-700">
                    We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.
                  </p>
                  <p className="text-gray-700">
                    You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-4">Contact Us</h2>
                  <p className="text-gray-700">
                    If you have any questions about this Privacy Policy, please contact us.
                  </p>
                </section>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
